import { Link } from 'react-router-dom';
import type { MouseEvent } from 'react';
import type { GenerationJob } from '@muidsoft/shared';
import { StatusStamp } from './StatusStamp.js';
import { IconRefresh, IconTrash } from './Icons.js';
import { api } from '../lib/api.js';

interface Props {
  job: GenerationJob;
  productName?: string;
  onRetried?: () => void;
  onDeleted?: (jobId: string) => void;
}

/**
 * Format ISO timestamp → waktu relatif singkat (mis. "3 mnt lalu", "2j lalu", "kemarin").
 * Tidak butuh library — cukup untuk tujuan ini.
 */
function relativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return '';
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'baru saja';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} mnt lalu`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}j lalu`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'kemarin';
  if (diffDay < 7) return `${diffDay} hari lalu`;
  // Tampilkan tanggal kalau sudah lebih dari seminggu
  return new Date(isoString).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
}

export function JobStatusCard({ job, productName, onRetried, onDeleted }: Props) {
  const fillClass =
    job.currentStep === 'failed' ? 'progress-fill progress-fill--error' :
    job.currentStep === 'completed' ? 'progress-fill progress-fill--success' :
    'progress-fill';

  // Ambil timestamp paling relevan: updatedAt (perubahan terakhir) atau startedAt
  const timestampStr = job.updatedAt ?? job.startedAt;
  const timeLabel = relativeTime(timestampStr);

  async function handleRetry(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/api/bff/jobs/${job.id}/retry`);
      onRetried?.();
    } catch {
      // biarkan user coba lagi manual
    }
  }

  async function handleDelete(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Hapus job ini? Tindakan tidak bisa dibatalkan.')) return;
    try {
      await api.delete(`/api/bff/jobs/${job.id}`);
      onDeleted?.(job.id);
    } catch {
      alert('Gagal menghapus job. Coba lagi.');
    }
  }

  const isTerminal = job.currentStep === 'failed' || job.currentStep === 'completed';

  return (
    <Link to={`/jobs/${job.id}`} className="job-status-card card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span className="text-muted mono" style={{ fontSize: 12 }}>
              {productName ?? job.productId} · #{job.id.slice(-6)}
            </span>
            {timeLabel && (
              <span className="text-muted" style={{ fontSize: 11 }}>· {timeLabel}</span>
            )}
          </div>
          <div style={{ marginTop: 4 }}>
            <StatusStamp status={job.currentStep} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {job.currentStep === 'failed' && (
            <button className="btn-ghost tap-target icon-btn" onClick={handleRetry} title="Coba Lagi">
              <IconRefresh size={16} />
              <span>Coba Lagi</span>
            </button>
          )}
          {isTerminal && (
            <button
              className="btn-ghost tap-target icon-btn"
              onClick={handleDelete}
              style={{ color: 'var(--status-error)', borderColor: 'var(--status-error)' }}
              title="Hapus job"
            >
              <IconTrash size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="progress-track">
        <div className={fillClass} style={{ width: `${job.progress}%` }} />
      </div>
      {job.currentStep === 'failed' && job.errorMessage && (
        <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
          {job.errorMessage}
        </p>
      )}
      {job.currentStep === 'exporting_prompts' && (
        <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
          Sedang membuat formatted prompts untuk semua scene...
        </p>
      )}
    </Link>
  );
}
